import { useEffect, useRef, useState } from "react";
import { MapPin, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, apiErrorMessage } from "@/lib/api";

const newSession = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now()));

function SuggestionList({ items, active, onPick, onHover }) {
  return (
    <ul className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg" role="listbox" data-testid="place-suggestions">
      {items.map((s, i) => (
        <li
          key={s.place_id}
          role="option"
          aria-selected={i === active}
          onMouseDown={(e) => { e.preventDefault(); onPick(s); }}
          onMouseEnter={() => onHover(i)}
          className={`flex items-start gap-2 px-3 py-2 cursor-pointer border-b border-slate-100 last:border-0 ${i === active ? "bg-sky-50" : "hover:bg-slate-50"}`}
          data-testid={`place-suggestion-${i}`}
        >
          <MapPin size={14} className="mt-0.5 shrink-0 text-sky-700" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate">{s.main_text || s.description}</div>
            <div className="text-[11px] text-slate-500 truncate">{s.secondary_text}</div>
          </div>
        </li>
      ))}
      <li className="px-3 py-1 text-[10px] text-right text-slate-400">powered by Google</li>
    </ul>
  );
}

export function PlaceAutocomplete({ value, place, onTextChange, onPlaceSelected, placeholder, testId }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [typed, setTyped] = useState(false);
  const session = useRef(newSession());

  useEffect(() => {
    const q = (value || "").trim();
    if (!typed || q.length < 2) { setItems([]); return undefined; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/sppd/places/autocomplete", { params: { q, session: session.current } });
        setItems(data || []);
        setActive(-1);
        setOpen(true);
      } catch (e) {
        setItems([]);
        toast.error(apiErrorMessage(e, "Gagal memuat saran lokasi"));
      } finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [value, typed]);

  async function pick(s) {
    setOpen(false);
    setTyped(false);
    setItems([]);
    try {
      const { data } = await api.get(`/sppd/places/details/${encodeURIComponent(s.place_id)}`, { params: { session: session.current } });
      onPlaceSelected({ ...data, description: s.description });
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal mengambil koordinat lokasi"));
    } finally {
      session.current = newSession();
    }
  }

  function onKeyDown(e) {
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); pick(items[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  }

  return (
    <div className="relative">
      <div className="relative mt-1.5">
        <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          className="pl-9 pr-9"
          value={value}
          placeholder={placeholder}
          autoComplete="off"
          onChange={(e) => { setTyped(true); onTextChange(e.target.value); }}
          onFocus={() => items.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={onKeyDown}
          data-testid={testId}
        />
        {loading && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />}
      </div>
      {open && items.length > 0 && <SuggestionList items={items} active={active} onPick={pick} onHover={setActive} />}
      {place && (
        <div className="mt-1 flex items-center gap-1 text-[11px] text-emerald-700" data-testid="place-selected-coords">
          <CheckCircle2 size={11} /> Titik lokasi tersimpan ({place.latitude.toFixed(5)}, {place.longitude.toFixed(5)})
        </div>
      )}
    </div>
  );
}
